import { useRef, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Panel, DefaultButton } from "@fluentui/react";
import readNDJSONStream from "ndjson-readablestream";

import appLogo from "../../assets/applogo.svg";
import styles from "./Chat.module.css";

import {
    chatApi,
    addCommentApi,
    configApi,
    RetrievalMode,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ResponseMessage,
    VectorFieldOptions,
    GPT4VInput,
    SpeechConfig,
    getAllowedUsersFromPostgresqlApi,
    getDevUsersFromPostgresqlApi,
    getUserCategoriesFromPostgresqlApi,
    getParameterValueApi
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { SettingsButton } from "../../components/SettingsButton";
import { ClearChatButton } from "../../components/ClearChatButton";
import { UploadFile } from "../../components/UploadFile";
import { useLogin, getToken, requireAccessControl, getUsername } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { LoginContext } from "../../loginContext";
import { LanguagePicker } from "../../i18n/LanguagePicker";
import { Settings } from "../../components/Settings/Settings";
import FileListByCategory from "../../components/FileListByCategory/FileListByCategory";
import { useAuth } from "../../auth/authProvider";
import { HistoryButton } from "../../components/HistoryButton";
import { HistoryPanel } from "../../components/HistoryPanel";
import { HistoryProviderOptions, useHistoryManager } from "../../components/HistoryProviders";

const Chat = () => {
    const { username, getAuthToken, isAuthenticated, isLocalAuth, logout, isLoading: isAuthLoading } = useAuth();

    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(1);
    const [seed, setSeed] = useState<number | null>(1);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(0);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(30);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(false);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    // const [includeCategory, setIncludeCategory] = useState<string[]>([""]);
    const [includeCategory, setIncludeCategory] = useState<string[]>([]);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);
    const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([VectorFieldOptions.Embedding]);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
    const [defaultLlmProvider, setDefaultLlmProvider] = useState<string | undefined>(undefined);
    const [llmProvider, setLlmProvider] = useState<string>("openai"); // Will be updated by config
    const [useGPT4V, setUseGPT4V] = useState<boolean>(false);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [speechUrls, setSpeechUrls] = useState<(string | null)[]>([]);

    const [showGPT4VOptions, setShowGPT4VOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] = useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const [showUserUpload, setShowUserUpload] = useState<boolean>(false);
    const [showLanguagePicker, setshowLanguagePicker] = useState<boolean>(false);
    const [showSpeechInput, setShowSpeechInput] = useState<boolean>(false);
    const [showSpeechOutputBrowser, setShowSpeechOutputBrowser] = useState<boolean>(false);
    const [showSpeechOutputAzure, setShowSpeechOutputAzure] = useState<boolean>(false);
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState<boolean>(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState<boolean>(false);
    const audio = useRef(new Audio()).current;
    const [isPlaying, setIsPlaying] = useState(false);

    const [fileData, setFileData] = useState<{ [key: string]: string[] }>({});

    const speechConfig: SpeechConfig = {
        speechUrls,
        setSpeechUrls,
        audio,
        isPlaying,
        setIsPlaying
    };

    const getConfig = async () => {
        configApi().then(async config => {
            setShowGPT4VOptions(config.showGPT4VOptions);
            setUseSemanticRanker(config.showSemanticRankerOption);
            setShowSemanticRankerOption(config.showSemanticRankerOption);
            setShowVectorOption(config.showVectorOption);
            if (!config.showVectorOption) {
                setRetrievalMode(RetrievalMode.Text);
            }
            setShowUserUpload(config.showUserUpload);
            setshowLanguagePicker(config.showLanguagePicker);
            setShowSpeechInput(config.showSpeechInput);
            setShowSpeechOutputBrowser(config.showSpeechOutputBrowser);
            setShowSpeechOutputAzure(config.showSpeechOutputAzure);
            setShowChatHistoryBrowser(config.showChatHistoryBrowser);
            setShowChatHistoryCosmos(config.showChatHistoryCosmos);
            const retrieveCountValue = await getParameterValueApi("retrieveCount");
            if (typeof retrieveCountValue === "number") {
                setRetrieveCount(retrieveCountValue);
            } else {
                setRetrieveCount(30); // Fallback to default if not a number
            }
            // Fetch and set the default LLM provider
            const backendDefaultLlmProvider = (await getParameterValueApi("DEFAULT_LLM_PROVIDER")) || config.defaultLlmProvider || "openai";
            if (typeof backendDefaultLlmProvider === "string") {
                setDefaultLlmProvider(backendDefaultLlmProvider);
                setLlmProvider(backendDefaultLlmProvider); // Initialize llmProvider with the default
            } else {
                setDefaultLlmProvider("openai");
                setLlmProvider("openai"); // Initialize llmProvider with the default
            }
        });
    };

    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], responseBody: ReadableStream<any>) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        message: { content: answer, role: askResponse.message.role }
                    };
                    setStreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (event["context"] && event["context"]["data_points"]) {
                    event["message"] = event["delta"];
                    askResponse = event as ChatAppResponse;
                } else if (event["delta"] && event["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["delta"]["content"]);
                } else if (event["context"]) {
                    // Update context with new keys from latest event
                    askResponse.context = { ...askResponse.context, ...event["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            message: { content: answer, role: askResponse.message.role }
        };
        return fullResponse;
    };

    const historyProvider: HistoryProviderOptions = (() => {
        if (isAuthenticated && showChatHistoryCosmos) return HistoryProviderOptions.CosmosDB;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();
    const historyManager = useHistoryManager(historyProvider);

    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        const token = await getAuthToken();

        try {
            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].message.content, role: "assistant" }
            ]);
            // Determine the final list of categories to include in the request.
            let finalIncludeCategory: string[];

            // Check if the user intends to include all categories (implicitly or explicitly)
            const selectAllCondition = includeCategory.includes("All") || includeCategory.length === 0;

            if (selectAllCondition) {
                // If selecting all, use the full list of the user's available categories.
                // This is inherently a subset of userCategories.
                finalIncludeCategory = userCategories;
            } else {
                // If specific categories are selected by the user,
                // filter the selection to ensure it only contains categories
                // that are currently valid/available for this user.
                // This guarantees the result is a subset of userCategories.
                finalIncludeCategory = includeCategory.filter(cat => userCategories.includes(cat));
            }

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        // include_category: includeCategory.length === 0 ? undefined : includeCategory,
                        include_category: finalIncludeCategory,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        top: retrieveCount,
                        temperature: temperature,
                        llm_provider: llmProvider, // Include llmProvider in overrides
                        minimum_reranker_score: minimumRerankerScore,
                        minimum_search_score: minimumSearchScore,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        vector_fields: vectorFieldList,
                        use_gpt4v: useGPT4V,
                        gpt4v_input: gpt4vInput,
                        language: i18n.language,
                        ...(seed !== null ? { seed: seed } : {})
                    }
                },
                // AI Chat Protocol: Client must pass on any session state received from the server
                session_state: answers.length ? answers[answers.length - 1][1].session_state : null
            };

            const response = await chatApi(request, shouldStream, token ?? undefined);
            if (!response.body) {
                throw Error("No response body");
            }
            if (response.status > 299 || !response.ok) {
                throw Error(`Request failed with status ${response.status}`);
            }
            if (shouldStream) {
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, answers, response.body);
                setAnswers([...answers, [question, parsedResponse]]);
                if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    const token = await getAuthToken();
                    historyManager.addItem(parsedResponse.session_state, [...answers, [question, parsedResponse]], token ?? undefined);
                }
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (parsedResponse.error) {
                    throw Error(parsedResponse.error);
                }
                setAnswers([...answers, [question, parsedResponse as ChatAppResponse]]);
                if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    const token = await getAuthToken();
                    historyManager.addItem(parsedResponse.session_state, [...answers, [question, parsedResponse as ChatAppResponse]], token ?? undefined);
                }
            }
            setSpeechUrls([...speechUrls, null]);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setSpeechUrls([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
    };

    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" }), [isLoading]);
    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" }), [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    const handleSettingsChange = (field: string, value: any) => {
        switch (field) {
            case "promptTemplate":
                setPromptTemplate(value);
                break;
            case "temperature":
                setTemperature(value);
                break;
            case "seed":
                setSeed(value);
                break;
            case "minimumRerankerScore":
                setMinimumRerankerScore(value);
                break;
            case "minimumSearchScore":
                setMinimumSearchScore(value);
                break;
            case "retrieveCount":
                setRetrieveCount(value);
                break;
            case "useSemanticRanker":
                setUseSemanticRanker(value);
                break;
            case "useSemanticCaptions":
                setUseSemanticCaptions(value);
                break;
            case "excludeCategory":
                setExcludeCategory(value);
                break;
            case "includeCategory":
                setIncludeCategory(value);
                break;
            case "useOidSecurityFilter":
                setUseOidSecurityFilter(value);
                break;
            case "useGroupsSecurityFilter":
                setUseGroupsSecurityFilter(value);
                break;
            case "shouldStream":
                setShouldStream(value);
                break;
            case "useSuggestFollowupQuestions":
                setUseSuggestFollowupQuestions(value);
                break;
            case "useGPT4V":
                setUseGPT4V(value);
                break;
            case "gpt4vInput":
                setGPT4VInput(value);
                break;
            case "vectorFieldList":
                setVectorFieldList(value);
                break;
            case "retrievalMode":
                setRetrievalMode(value);
                break;
            case "llm_provider": // Handle llm_provider change
                setLlmProvider(value);
                break;
        }
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(undefined);
            setActiveAnalysisPanelTab(undefined);
            // wait 1 milli-second before showing citation
            setTimeout(() => {
                setActiveCitation(citation);
                setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
            }, 1);

            // setActiveCitation(citation);
            // setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const { t, i18n } = useTranslation();

    // const devUsers: string[] = ["hoang-viet-tuan.nguyen@vinci-energies.net", ""];
    // const allowedUsers: string[] = [
    //     "hoang-viet-tuan.nguyen@vinci-energies.net",
    //     "nicolas.poulain@vinci-energies.com",
    //     "mohammed.boutoub@vinci-energies.net",
    //     "ismail.rahimi@vinci-energies.net",
    //     "arounan.kone@vinci-energies.net"
    // ];

    const [allowedUsers, setAllowedUsers] = useState<string[]>([]); // Initialize as an empty array
    const [isLoadingAllowedUsers, setIsLoadingAllowedUsers] = useState<boolean>(true); // New state for loading status

    useEffect(() => {
        const fetchAllowedUsers = async () => {
            try {
                setIsLoadingAllowedUsers(true);
                const users = await getAllowedUsersFromPostgresqlApi();
                setAllowedUsers(users);
            } catch (error) {
                console.error("Error fetching allowed users:", error);
                // Handle the error appropriately, e.g., display an error message to the user
                // or set a default list of allowed users.
                setAllowedUsers(["xxx.xx@vinci-energies.net"]); // Default user in case of error
            } finally {
                setIsLoadingAllowedUsers(false);
            }
        };

        fetchAllowedUsers();
    }, []);

    const [userCategories, setUserCategories] = useState<string[]>([]); // Initialize as an empty array

    useEffect(() => {
        const fetchUserCategories = async () => {
            if (!username) return; // Prevent API call if username is empty

            try {
                const categories = await getUserCategoriesFromPostgresqlApi(username);
                setUserCategories(categories);
            } catch (error) {
                console.error("Error fetching user categories:", error);
            }
        };

        fetchUserCategories();
    }, [username]); // Depend on `username` instead of running on the first render

    const [devUsers, setDevUsers] = useState<string[]>([]); // Initialize as an empty array

    useEffect(() => {
        const fetchDevUsers = async () => {
            try {
                const users = await getDevUsersFromPostgresqlApi();
                setDevUsers(users);
            } catch (error) {
                console.error("Error fetching allowed users:", error);
                // Handle the error appropriately, e.g., display an error message to the user
                // or set a default list of allowed users.
                setDevUsers(["xxx.xx@vinci-energies.net"]); // Default user in case of error
            }
        };

        fetchDevUsers();
    }, []);

    const showCustomAlert = (message: string) => {
        // Create a custom alert div
        const alertDiv = document.createElement("div");
        alertDiv.style.position = "fixed";
        alertDiv.style.top = "20px";
        alertDiv.style.left = "50%";
        alertDiv.style.transform = "translateX(-50%)";
        alertDiv.style.padding = "10px 20px";
        alertDiv.style.backgroundColor = "#b7cbf7"; // Light blue background
        alertDiv.style.color = "black"; // Text color
        alertDiv.style.borderRadius = "5px";
        alertDiv.style.fontSize = "16px";
        alertDiv.style.zIndex = "1000";
        alertDiv.style.textAlign = "center";
        alertDiv.style.display = "flex"; // Use flexbox
        alertDiv.style.flexDirection = "column"; // Stack items vertically
        alertDiv.style.alignItems = "center"; // Center horizontally
        alertDiv.style.justifyContent = "center"; // Center vertically
        alertDiv.style.gap = "10px";

        // Append the custom alert div to the body
        document.body.appendChild(alertDiv);

        // Create the text content div
        const textDiv = document.createElement("div");
        textDiv.innerText = message;
        alertDiv.appendChild(textDiv);

        // Add validate button to remove the alert
        const validateButton = document.createElement("button");
        validateButton.innerText = "Valider";
        validateButton.style.padding = "5px 10px";
        validateButton.style.backgroundColor = "#4CAF50";
        validateButton.style.color = "white";
        validateButton.style.border = "none";
        validateButton.style.borderRadius = "5px";
        validateButton.style.cursor = "pointer";
        alertDiv.appendChild(validateButton);

        // Function to remove the alert
        const removeAlert = () => {
            if (alertDiv) {
                alertDiv.remove();
            }
        };

        // Remove the alert when clicked on "Validate"
        validateButton.addEventListener("click", removeAlert);

        // Automatically remove the alert after 10 seconds
        setTimeout(removeAlert, 10000);
    };

    useEffect(() => {
        getConfig();
        // Show message at the first load
        showCustomAlert(
            "Cet outil est en cours de développement et peut fournir des réponses inexactes. Afin d'améliorer la qualité des réponses, nous vous invitons à partager vos commentaires aussi fréquemment que possible !"
        );
    }, []);

    const formatLlmProviderName = (provider: string): string => {
        if (provider.toLowerCase() === "openai") {
            return "OpenAI";
        }
        return provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
    };

    return (
        <div className={styles.container}>
            {/* Setting the page title using react-helmet-async */}
            <Helmet>
                <title>{t("pageTitle")}</title>
            </Helmet>
            <div className={styles.commandsSplitContainer}>
                <div className={styles.commandsContainer}>
                    {((useLogin() && showChatHistoryCosmos) || showChatHistoryBrowser) && (
                        <HistoryButton className={styles.commandButton} onClick={() => setIsHistoryPanelOpen(!isHistoryPanelOpen)} />
                    )}
                </div>
                <div className={styles.commandsContainer}>
                    <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />
                    {showUserUpload && <UploadFile className={styles.commandButton} disabled={!isAuthenticated} />}
                    {/* Afficher ou pas les paramétrages */}
                    <SettingsButton className={styles.commandButton} onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)} />
                </div>
            </div>
            <div className={styles.chatRoot} style={{ marginLeft: isHistoryPanelOpen ? "300px" : "0" }}>
                {isLoadingAllowedUsers ? (
                    <div>{t("loading")}</div> // Or any other loading indicator
                ) : useLogin() && !allowedUsers.includes(username) ? (
                    <div className={styles.accessDeniedMessage}>
                        <p>
                            L'utilisateur n'a pas accès à l'outil, merci de faire une demande d'accès à{" "}
                            <a href="mailto:tuan.nguyen@actemium.com">tuan.nguyen@actemium.com</a>
                        </p>
                    </div>
                ) : (
                    <div className={styles.chatContainer}>
                        {!lastQuestionRef.current ? (
                            <div className={styles.chatEmptyState}>
                                <img src={appLogo} alt="App logo" width="120" height="120" />
                                <h1 className={styles.chatEmptyStateTitle}>{t("chatEmptyStateTitle")}</h1>
                                <h2 className={styles.chatEmptyStateSubtitle}>{t("chatEmptyStateSubtitle")}</h2>
                                {showLanguagePicker && <LanguagePicker onLanguageChange={newLang => i18n.changeLanguage(newLang)} />}
                                <ExampleList
                                    onExampleClicked={onExampleClicked}
                                    selectedCategories={includeCategory} // Pass the state from settings
                                    userCategories={userCategories} // Pass the user's available categories
                                />
                            </div>
                        ) : (
                            <div className={styles.chatMessageStream}>
                                {isStreaming &&
                                    streamedAnswers.map((streamedAnswer, index) => (
                                        <div key={index}>
                                            <UserChatMessage message={streamedAnswer[0]} />
                                            <div className={styles.chatMessageGpt}>
                                                <Answer
                                                    isStreaming={true}
                                                    key={index}
                                                    answer={streamedAnswer[1]}
                                                    index={index}
                                                    speechConfig={speechConfig}
                                                    isSelected={false}
                                                    onCitationClicked={c => onShowCitation(c, index)}
                                                    onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                    onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                    onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                    showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                    showSpeechOutputAzure={showSpeechOutputAzure}
                                                    showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                    question={lastQuestionRef.current}
                                                    retrieveCount={retrieveCount}
                                                    retrievalMode={retrievalMode}
                                                    isLastAnswer={answers.length - 1 === index}
                                                    model={
                                                        (streamedAnswer[1].context?.thoughts?.find(t => t.title === "chat_query_rewrite")?.props
                                                            ?.model as string) || llmProvider
                                                    }
                                                    categories={includeCategory}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                {!isStreaming &&
                                    answers.map((answer, index) => (
                                        <div key={index}>
                                            <UserChatMessage message={answer[0]} />
                                            <div className={styles.chatMessageGpt}>
                                                <Answer
                                                    isStreaming={false}
                                                    key={index}
                                                    answer={answer[1]}
                                                    index={index}
                                                    speechConfig={speechConfig}
                                                    isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                    onCitationClicked={c => onShowCitation(c, index)}
                                                    onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                    onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                    onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                    showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                    showSpeechOutputAzure={showSpeechOutputAzure}
                                                    showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                    question={lastQuestionRef.current}
                                                    retrieveCount={retrieveCount}
                                                    retrievalMode={retrievalMode}
                                                    isLastAnswer={answers.length - 1 === index}
                                                    model={
                                                        (answer[1].context?.thoughts?.find(t => t.title === "chat_query_rewrite")?.props?.model as string) ||
                                                        llmProvider
                                                    }
                                                    categories={includeCategory}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                {isLoading && (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerLoading />
                                        </div>
                                    </>
                                )}
                                {error ? (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                        </div>
                                    </>
                                ) : null}
                                <div ref={chatMessageStreamEnd} />
                            </div>
                        )}
                        <div className={styles.chatInput}>
                            <QuestionInput
                                clearOnSend
                                placeholder={`${t("defaultExamples.placeholder")} (à ${formatLlmProviderName(llmProvider)})`}
                                disabled={isLoading}
                                onSend={question => makeApiRequest(question)}
                                showSpeechInput={showSpeechInput}
                            />
                        </div>
                    </div>
                )}

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}

                {((useLogin() && showChatHistoryCosmos) || showChatHistoryBrowser) && (
                    <HistoryPanel
                        provider={historyProvider}
                        isOpen={isHistoryPanelOpen}
                        notify={!isStreaming && !isLoading}
                        onClose={() => setIsHistoryPanelOpen(false)}
                        onChatSelected={answers => {
                            if (answers.length === 0) return;
                            setAnswers(answers);
                            lastQuestionRef.current = answers[answers.length - 1][0];
                        }}
                    />
                )}

                <Panel
                    headerText={t("labels.headerText")}
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel={t("labels.closeButton")}
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>{t("labels.closeButton")}</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <Settings
                        promptTemplate={promptTemplate}
                        temperature={temperature}
                        retrieveCount={retrieveCount}
                        seed={seed}
                        minimumSearchScore={minimumSearchScore}
                        minimumRerankerScore={minimumRerankerScore}
                        useSemanticRanker={useSemanticRanker}
                        useSemanticCaptions={useSemanticCaptions}
                        excludeCategory={excludeCategory}
                        includeCategory={includeCategory}
                        retrievalMode={retrievalMode}
                        useGPT4V={useGPT4V}
                        gpt4vInput={gpt4vInput}
                        vectorFieldList={vectorFieldList}
                        showSemanticRankerOption={showSemanticRankerOption}
                        showGPT4VOptions={showGPT4VOptions}
                        showVectorOption={showVectorOption}
                        useOidSecurityFilter={useOidSecurityFilter}
                        useGroupsSecurityFilter={useGroupsSecurityFilter}
                        useLogin={!!useLogin()}
                        loggedIn={isAuthenticated}
                        requireAccessControl={requireAccessControl()}
                        shouldStream={shouldStream}
                        useSuggestFollowupQuestions={useSuggestFollowupQuestions}
                        showSuggestFollowupQuestions={true}
                        onChange={handleSettingsChange}
                        isDevUser={devUsers.includes(username)}
                        llmProvider={llmProvider} // Pass llmProvider to Settings
                        defaultLlmProvider={defaultLlmProvider} // Pass defaultLlmProvider to Settings
                        categories={userCategories}
                    />
                    {/* {devUsers.includes(username) && useLogin && <TokenClaimsDisplay />} */}

                    {/* Fetch files */}
                    <FileListByCategory username={username} setFileData={setFileData} />

                    <div>
                        <h2 className="text-xl font-bold">Liste des fichiers par catégorie:</h2>
                        <p className="text-gray-600 text-sm mb-2">Cette section affiche les fichiers disponibles pour {username}.</p>

                        {Object.keys(fileData).length > 0 ? (
                            <div className="space-y-4">
                                {Object.entries(fileData).map(([category, files]) => (
                                    <div key={category} className="p-4 border rounded-lg shadow">
                                        <h3 className="text-lg font-semibold">{category}</h3>
                                        {files.length > 0 ? (
                                            <ul className="list-disc pl-5">
                                                {files.map((file, index) => (
                                                    <li key={index} className="text-gray-700">
                                                        {file}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-gray-500 italic">Aucun fichier dans cette catégorie.</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">Aucun fichier disponible pour l'utilisateur {username}.</p>
                        )}
                    </div>
                </Panel>
            </div>
        </div>
    );
};

export default Chat;
